import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useEffect,
	type ReactNode,
	useRef,
} from "react";
import type { Tab } from "./tabs-context";
import { trpc } from "@/utils/trpc";
import { useMutation } from "@tanstack/react-query";
import { Route as WorkspaceRoute } from "@/routes/$workspaceId/route";
import { useWorkspaceContext } from "./workspace-context";

export type PanelPosition = "center" | "left" | "right" | "top" | "bottom";
export type SplitOrientation = "horizontal" | "vertical";

// ============================================================================
// NEW CLEAN DATA MODEL - Tree-based, no redundancy
// ============================================================================

// A layout node is either a panel or a group containing children
export type LayoutNode =
	| { type: "panel"; id: string; size?: number }
	| { type: "group"; id: string; orientation: SplitOrientation; children: LayoutNode[]; size?: number };

// Panel type for external consumers (backwards compatible)
export interface Panel {
	id: string;
	size?: number;
}

// The entire layout is just a tree + focused panel
export interface SplitLayout {
	root: LayoutNode;
	focusedPanelId: string;
}

// ============================================================================
// TREE HELPER FUNCTIONS
// ============================================================================

// Find a node by ID in the tree
function findNode(node: LayoutNode, targetId: string): LayoutNode | null {
	if (node.id === targetId) return node;
	if (node.type === "group") {
		for (const child of node.children) {
			const found = findNode(child, targetId);
			if (found) return found;
		}
	}
	return null;
}

// Find the parent group of a node by ID
function findParent(root: LayoutNode, targetId: string): (LayoutNode & { type: "group" }) | null {
	if (root.type !== "group") return null;
	
	for (const child of root.children) {
		if (child.id === targetId) return root;
		if (child.type === "group") {
			const found = findParent(child, targetId);
			if (found) return found;
		}
	}
	return null;
}

// Get all panels as a flat array
function getAllPanelsFromTree(node: LayoutNode): Panel[] {
	if (node.type === "panel") {
		return [{ id: node.id, size: node.size }];
	}
	return node.children.flatMap(child => getAllPanelsFromTree(child));
}

// Deep clone a layout node
function cloneNode(node: LayoutNode): LayoutNode {
	if (node.type === "panel") {
		return { ...node };
	}
	return {
		...node,
		children: node.children.map(cloneNode),
	};
}

// Remove a node from tree, returns new root (or null if tree becomes empty)
function removeNode(root: LayoutNode, targetId: string): LayoutNode | null {
	// Can't remove the root itself if it's the target
	if (root.id === targetId) return null;
	
	if (root.type !== "group") return root;
	
	const newChildren: LayoutNode[] = [];
	for (const child of root.children) {
		if (child.id === targetId) {
			// Skip this child (remove it)
			continue;
		}
		if (child.type === "group") {
			const result = removeNode(child, targetId);
			if (result) newChildren.push(result);
		} else {
			newChildren.push(child);
		}
	}
	
	// If group now has no children, return null (will be removed by parent)
	if (newChildren.length === 0) return null;
	
	// If group has only one child, promote that child (collapse the group)
	if (newChildren.length === 1) {
		const onlyChild = newChildren[0];
		// Preserve the group's size on the promoted child
		return { ...onlyChild, size: root.size };
	}
	
	return { ...root, children: newChildren };
}

// Replace a node in the tree with a new node
function replaceNode(root: LayoutNode, targetId: string, newNode: LayoutNode): LayoutNode {
	if (root.id === targetId) return newNode;
	
	if (root.type !== "group") return root;
	
	return {
		...root,
		children: root.children.map(child => replaceNode(child, targetId, newNode)),
	};
}

// ============================================================================
// ID GENERATORS
// ============================================================================

const generatePanelId = () =>
	`panel-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const generateGroupId = () =>
	`group-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

// ============================================================================
// DEFAULT LAYOUT
// ============================================================================

const getDefaultLayout = (): SplitLayout => ({
	root: { type: "panel", id: "default", size: 100 },
	focusedPanelId: "default",
});

// ============================================================================
// MIGRATION FROM OLD FORMAT
// ============================================================================

interface OldPanelGroup {
	id: string;
	orientation: SplitOrientation;
	panels: string[];
	groups: string[];
	children?: Array<{ type: "panel" | "group"; id: string }>;
	size?: number;
}

interface OldSplitLayout {
	panels: Record<string, { id: string; size?: number }>;
	groups: Record<string, OldPanelGroup>;
	rootGroupId: string;
	focusedPanelId: string;
}

function migrateOldLayout(old: OldSplitLayout): SplitLayout {
	// Recursive function to build tree from old format
	function buildNode(groupId: string): LayoutNode | null {
		const group = old.groups[groupId];
		if (!group) return null;
		
		const children: LayoutNode[] = [];
		
		// Use children array if available (preserves order), otherwise fall back
		const orderedChildren = group.children && group.children.length > 0
			? group.children
			: [
				...group.panels.map(id => ({ type: "panel" as const, id })),
				...group.groups.map(id => ({ type: "group" as const, id })),
			];
		
		for (const childRef of orderedChildren) {
			if (childRef.type === "panel") {
				const panel = old.panels[childRef.id];
				if (panel) {
					children.push({ type: "panel", id: panel.id, size: panel.size });
				}
			} else {
				const childGroup = buildNode(childRef.id);
				if (childGroup) children.push(childGroup);
			}
		}
		
		// If no children, skip this group
		if (children.length === 0) return null;
		
		// If only one child, just return that child (no need for wrapper group)
		if (children.length === 1) {
			return { ...children[0], size: group.size };
		}
		
		return {
			type: "group",
			id: group.id,
			orientation: group.orientation,
			children,
			size: group.size,
		};
	}
	
	const root = buildNode(old.rootGroupId);
	
	// If migration failed, return default
	if (!root) {
		console.warn("Migration failed, using default layout");
		return getDefaultLayout();
	}
	
	// Validate focused panel exists
	const allPanels = getAllPanelsFromTree(root);
	const focusedPanelId = allPanels.some(p => p.id === old.focusedPanelId)
		? old.focusedPanelId
		: allPanels[0]?.id || "default";
	
	return { root, focusedPanelId };
}

// Check if layout is old format
function isOldFormat(layout: any): layout is OldSplitLayout {
	return layout && typeof layout === "object" && "panels" in layout && "groups" in layout && "rootGroupId" in layout;
}

// Check if layout is new format
function isNewFormat(layout: any): layout is SplitLayout {
	return layout && typeof layout === "object" && "root" in layout && layout.root && "type" in layout.root;
}

// ============================================================================
// CONTEXT TYPE
// ============================================================================

interface SplitPanelContextType {
	layout: SplitLayout;
	focusedPanelId: string;
	panels: Panel[];
	createPanel: (position?: PanelPosition, sourceTab?: Tab) => string;
	removePanel: (panelId: string) => void;
	focusPanel: (panelId: string) => void;
	splitPanel: (
		sourcePanelId: string,
		direction: "left" | "right" | "up" | "down",
		tab?: Tab,
	) => string;
	splitPanelWithDrop: (
		sourcePanelId: string,
		position: PanelPosition,
		tab: Tab,
	) => string;
	moveTabToPanel: (tab: Tab, targetPanelId: string) => void;
	setPanelSize: (panelId: string, size: number) => void;
	getPanelById: (panelId: string) => Panel | undefined;
	getAllPanels: () => Panel[];
	mergePanels: (sourcePanelId: string, targetPanelId: string) => void;
	resetLayout: () => void;
}

const SplitPanelContext = createContext<SplitPanelContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export const SplitPanelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const { currentWorkspace } = WorkspaceRoute.useLoaderData();
	const { panelState, panelStateLoading, tabStateLoading } = useWorkspaceContext();
	const hasSavedLayoutRef = useRef(false);
	const currentWorkspaceId = currentWorkspace.id;

	const updatePanelStateMutationWebSocket = useMutation(
		trpc.me.updatePanelStateWebSocket.mutationOptions({}),
	);

	// Initialize layout from server state
	const [layout, setLayout] = useState<SplitLayout>(() => {
		if (panelStateLoading && tabStateLoading) {
			return getDefaultLayout();
		}
		
		// Handle migration or use new format
		if (isNewFormat(panelState)) {
			return panelState;
		} else if (isOldFormat(panelState)) {
			console.log("Migrating old layout format to new tree format");
			return migrateOldLayout(panelState);
		}
		
		return getDefaultLayout();
	});

	// Reset save flag when workspace changes
	useEffect(() => {
		hasSavedLayoutRef.current = false;
	}, [currentWorkspaceId]);

	// Update layout when workspace changes
	useEffect(() => {
		if (panelStateLoading && tabStateLoading) return;

		if (isNewFormat(panelState)) {
			setLayout(panelState);
		} else if (isOldFormat(panelState)) {
			console.log("Migrating old layout format to new tree format");
			setLayout(migrateOldLayout(panelState));
		} else {
			setLayout(getDefaultLayout());
		}
	}, [currentWorkspaceId, panelStateLoading, tabStateLoading, panelState]);

	// Persist layout changes
	useEffect(() => {
		if (panelStateLoading && tabStateLoading) return;
		
		if (!hasSavedLayoutRef.current) {
			hasSavedLayoutRef.current = true;
			return;
		}

		updatePanelStateMutationWebSocket.mutate({
			workspaceId: Number(currentWorkspaceId),
			panelState: layout as any, // Type cast for backend compatibility
		});
	}, [layout, currentWorkspaceId, panelStateLoading, tabStateLoading]);

	// ========================================================================
	// CONTEXT METHODS
	// ========================================================================

	const getAllPanels = useCallback((): Panel[] => {
		return getAllPanelsFromTree(layout.root);
	}, [layout.root]);

	const getPanelById = useCallback((panelId: string): Panel | undefined => {
		const node = findNode(layout.root, panelId);
		if (node && node.type === "panel") {
			return { id: node.id, size: node.size };
		}
		return undefined;
	}, [layout.root]);

	const focusPanel = useCallback((panelId: string) => {
		setLayout(prev => ({ ...prev, focusedPanelId: panelId }));
	}, []);

	const createPanel = useCallback((position: PanelPosition = "right", sourceTab?: Tab): string => {
		const newPanelId = generatePanelId();

		setLayout(prev => {
			const newRoot = cloneNode(prev.root);
			
			// If root is a single panel, wrap it in a group with the new panel
			if (newRoot.type === "panel") {
				const orientation: SplitOrientation = 
					position === "left" || position === "right" ? "horizontal" : "vertical";
				const newPanel: LayoutNode = { type: "panel", id: newPanelId };
				
				return {
					root: {
						type: "group",
						id: generateGroupId(),
						orientation,
						children: position === "left" || position === "top"
							? [newPanel, newRoot]
							: [newRoot, newPanel],
					},
					focusedPanelId: newPanelId,
				};
			}
			
			// Otherwise add to root group
			const newPanel: LayoutNode = { type: "panel", id: newPanelId };
			newRoot.children = position === "left" || position === "top"
				? [newPanel, ...newRoot.children]
				: [...newRoot.children, newPanel];
			
			return { root: newRoot, focusedPanelId: newPanelId };
		});

		return newPanelId;
	}, []);

	const removePanel = useCallback((panelId: string) => {
		setLayout(prev => {
			const newRoot = removeNode(cloneNode(prev.root), panelId);
			
			// If tree is empty, reset to default
			if (!newRoot) {
				return getDefaultLayout();
			}
			
			// Update focused panel if needed
			const allPanels = getAllPanelsFromTree(newRoot);
			const newFocusedId = allPanels.some(p => p.id === prev.focusedPanelId)
				? prev.focusedPanelId
				: allPanels[0]?.id || "default";
			
			return { root: newRoot, focusedPanelId: newFocusedId };
		});
	}, []);

	const splitPanelWithDrop = useCallback((sourcePanelId: string, position: PanelPosition, tab: Tab): string => {
		if (position === "center") {
			return sourcePanelId;
		}

		const newPanelId = generatePanelId();

		setLayout(prev => {
			const newRoot = cloneNode(prev.root);
			const sourceNode = findNode(newRoot, sourcePanelId);

			if (!sourceNode || sourceNode.type !== "panel") return prev;

			const isHorizontal = position === "left" || position === "right";
			const orientation: SplitOrientation = isHorizontal ? "horizontal" : "vertical";

			// Find parent to check if we can add to existing group
			const parent = findParent(newRoot, sourcePanelId);

			if (parent && parent.orientation === orientation) {
				// Add to existing group at the right position
				// Calculate equal size for all children after adding new panel
				const newChildCount = parent.children.length + 1;
				const equalSize = 100 / newChildCount;

				// Update all existing children to have equal size
				for (const child of parent.children) {
					child.size = equalSize;
				}

				const newPanel: LayoutNode = { type: "panel", id: newPanelId, size: equalSize };
				const sourceIndex = parent.children.findIndex(c => c.id === sourcePanelId);
				if (sourceIndex !== -1) {
					const insertIndex = position === "left" || position === "top"
						? sourceIndex
						: sourceIndex + 1;
					parent.children.splice(insertIndex, 0, newPanel);
				}
				return { root: newRoot, focusedPanelId: newPanelId };
			}

			// Need to create a new group wrapping the source panel
			// Both panels get equal 50% size
			const newPanel: LayoutNode = { type: "panel", id: newPanelId, size: 50 };
			const sourceClone: LayoutNode = { ...sourceNode, size: 50 };

			const newGroup: LayoutNode = {
				type: "group",
				id: generateGroupId(),
				orientation,
				children: position === "left" || position === "top"
					? [newPanel, sourceClone]
					: [sourceClone, newPanel],
				size: sourceNode.size,
			};

			// Replace source panel with the new group
			const resultRoot = replaceNode(newRoot, sourcePanelId, newGroup);

			return { root: resultRoot, focusedPanelId: newPanelId };
		});

		return newPanelId;
	}, []);

	const splitPanel = useCallback((
		sourcePanelId: string,
		direction: "left" | "right" | "up" | "down",
		tab?: Tab,
	): string => {
		const position: PanelPosition =
			direction === "left" ? "left"
			: direction === "right" ? "right"
			: direction === "up" ? "top"
			: "bottom";
		return splitPanelWithDrop(sourcePanelId, position, tab || ({} as Tab));
	}, [splitPanelWithDrop]);

	const moveTabToPanel = useCallback((tab: Tab, targetPanelId: string) => {
		// Tab management is handled by tabs-context, just focus the panel
		setLayout(prev => ({ ...prev, focusedPanelId: targetPanelId }));
	}, []);

	const setPanelSize = useCallback((panelId: string, size: number) => {
		setLayout(prev => {
			const newRoot = cloneNode(prev.root);
			const node = findNode(newRoot, panelId);
			if (node) {
				node.size = size;
			}
			return { ...prev, root: newRoot };
		});
	}, []);

	const mergePanels = useCallback((sourcePanelId: string, targetPanelId: string) => {
		setLayout(prev => {
			const newRoot = removeNode(cloneNode(prev.root), sourcePanelId);
			if (!newRoot) return getDefaultLayout();
			return { root: newRoot, focusedPanelId: targetPanelId };
		});
	}, []);

	const resetLayout = useCallback(() => {
		setLayout(getDefaultLayout());
	}, []);

	return (
		<SplitPanelContext.Provider
			value={{
				layout,
				focusedPanelId: layout.focusedPanelId,
				panels: getAllPanels(),
				createPanel,
				removePanel,
				focusPanel,
				splitPanel,
				splitPanelWithDrop,
				moveTabToPanel,
				setPanelSize,
				getPanelById,
				getAllPanels,
				mergePanels,
				resetLayout,
			}}
		>
			{children}
		</SplitPanelContext.Provider>
	);
};

export const useSplitPanel = () => {
	const context = useContext(SplitPanelContext);
	if (!context) {
		throw new Error("useSplitPanel must be used within a SplitPanelProvider");
	}
	return context;
};
